import {
    Image,
    User,
    Button,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Tabs,
    Tab,
    Tooltip,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
} from "@heroui/react";
import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LuLogOut,
    LuSettings,
    LuEllipsisVertical,
    LuPanelLeft,
    LuSearch,
    LuSun,
    LuMoon,
    LuCircleUserRound,
} from "react-icons/lu";
import clsx from "clsx";
import SidebarMenu from "./SidebarMenu";
import { LOGO_SQUARE_URL } from "@/constants";
import { useTheme, useUser } from "@/context";
import { LiveClock, NoImageFallback, ThemedText } from "@/components/ui";
import SearchModal from "./SearchModal";

const Sidebar = ({
    menuItems,
    isCollapsed,
    toggleSidebar,
    isMobileOpen,
    onMobileClose,
}) => {
    const navigate = useNavigate();
    const { theme, toggleTheme, setTheme } = useTheme();
    const { user, logout } = useUser();
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    // Ctrl+K shortcut to open search
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                e.preventDefault();
                setIsSearchOpen(true);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Default placeholder if user is not loaded yet
    const displayUser = user || {
        name: "Loading...",
        email: "...",
        avatar: null,
    };

    const modalLogout = useDisclosure();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleConfirmLogout = async () => {
        setIsLoggingOut(true);
        await logout();
    };

    return (
        <>
            {/* Mobile Backdrop */}
            <div
                className={clsx(
                    "fixed inset-0 z-40 bg-black/50 transition-opacity lg:pointer-events-none lg:hidden lg:opacity-0",
                    isMobileOpen
                        ? "opacity-100"
                        : "pointer-events-none opacity-0",
                )}
                onClick={onMobileClose}
            />

            <aside
                className={clsx(
                    "fixed top-0 left-0 z-50 h-screen flex-col border-r border-zinc-200 bg-white transition-all dark:border-zinc-800 dark:bg-zinc-900",
                    // Mobile: translate off-screen by default, slide in when open
                    isMobileOpen
                        ? "flex w-64 translate-x-0"
                        : "-translate-x-full",
                    // Desktop: always visible, override mobile translate
                    "lg:flex lg:translate-x-0",
                    isCollapsed ? "lg:w-18" : "lg:w-64",
                )}
            >
                {/* 1. Header: Fixed Top */}
                <div
                    className={clsx(
                        "group relative flex h-16 shrink-0 items-center px-4",
                        isCollapsed ? "justify-center" : "justify-between",
                    )}
                >
                    <NavLink
                        to="/"
                        className={clsx(
                            "flex items-center gap-2 transition-all duration-300",
                            isCollapsed &&
                                "group-hover:scale-50 group-hover:opacity-0",
                        )}
                    >
                        <Image
                            src={LOGO_SQUARE_URL}
                            alt="Logo"
                            classNames={{
                                wrapper: "w-8 h-8",
                                img: "w-full h-full object-contain",
                            }}
                            radius="none"
                        />
                    </NavLink>

                    {/* Action Buttons: Search & Toggle */}
                    <div
                        className={clsx(
                            "flex items-center gap-1",
                            isCollapsed ? "flex-col gap-4" : "",
                        )}
                    >
                        {!isCollapsed && (
                            <Tooltip content="Cari Menu (Ctrl+K)">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                                    onPress={() => setIsSearchOpen(true)}
                                >
                                    <LuSearch size={18} />
                                </Button>
                            </Tooltip>
                        )}

                        <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={
                                isMobileOpen ? onMobileClose : toggleSidebar
                            }
                            className={clsx(
                                "text-zinc-400 transition-all duration-300 hover:text-zinc-600 dark:hover:text-zinc-200",
                                isCollapsed &&
                                    "absolute inset-0 m-auto scale-50 rotate-180 opacity-0 group-hover:scale-100 group-hover:opacity-100",
                            )}
                        >
                            <LuPanelLeft size={18} />
                        </Button>
                    </div>
                </div>

                {/* 2. Body: Scrollable Menu */}
                <SidebarMenu
                    items={menuItems}
                    isCollapsed={isCollapsed}
                    onNavigate={onMobileClose}
                />

                {/* 3. Footer: Fixed Bottom */}
                <div className="flex shrink-0 flex-col">
                    {/* Theme Toggle Tab - Above the colored footer */}
                    <div className={clsx("px-4 pb-4", isCollapsed && "px-0")}>
                        <div
                            className={clsx(
                                "flex items-center",
                                isCollapsed && "justify-center",
                            )}
                        >
                            {isCollapsed ? (
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="flat"
                                    radius="full"
                                    onPress={toggleTheme}
                                    className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
                                >
                                    {theme === "dark" ? (
                                        <LuMoon size={18} />
                                    ) : (
                                        <LuSun size={18} />
                                    )}
                                </Button>
                            ) : (
                                <Tabs
                                    fullWidth
                                    size="sm"
                                    radius="full"
                                    selectedKey={theme}
                                    onSelectionChange={setTheme}
                                    classNames={{
                                        tabList:
                                            "bg-zinc-100 dark:bg-zinc-900/50 p-1",
                                        cursor: "bg-white dark:bg-zinc-800 shadow-sm",
                                        tabContent:
                                            "group-data-[selected=true]:text-zinc-900 dark:group-data-[selected=true]:text-zinc-100 text-zinc-500 dark:text-zinc-400 font-medium",
                                    }}
                                >
                                    <Tab
                                        key="light"
                                        title={
                                            <div className="flex items-center gap-2">
                                                <LuSun size={14} />
                                                <span>Light</span>
                                            </div>
                                        }
                                    />
                                    <Tab
                                        key="dark"
                                        title={
                                            <div className="flex items-center gap-2">
                                                <LuMoon size={14} />
                                                <span>Dark</span>
                                            </div>
                                        }
                                    />
                                </Tabs>
                            )}
                        </div>
                    </div>

                    {/* User Info & Live Clock - Colored Background */}
                    <div className="border-t border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div
                            className={clsx(
                                "flex w-full items-center rounded-xl border border-transparent",
                                isCollapsed
                                    ? "justify-center bg-transparent"
                                    : "mb-4 justify-between gap-2",
                            )}
                        >
                            {!isCollapsed && (
                                <User
                                    name={displayUser.name}
                                    description={user?.roles?.[0] || displayUser.email}
                                    avatarProps={{
                                        src: displayUser.avatar,
                                        size: "sm",
                                        showFallback: true,
                                        fallback: (
                                            <NoImageFallback
                                                icon={LuCircleUserRound}
                                                iconSize={16}
                                                text={displayUser.name}
                                            />
                                        ),
                                    }}
                                    classNames={{
                                        name: "text-sm font-semibold text-zinc-900 dark:text-zinc-100",
                                        description: "text-xs text-zinc-500",
                                        base: "justify-start gap-3",
                                    }}
                                />
                            )}

                            {isCollapsed ? (
                                <Dropdown placement="bottom-start">
                                    <DropdownTrigger>
                                        <User
                                            name=""
                                            description=""
                                            avatarProps={{
                                                src: displayUser.avatar,
                                                size: "sm",
                                                className: "cursor-pointer",
                                                showFallback: true,
                                                fallback: (
                                                    <NoImageFallback
                                                        icon={LuCircleUserRound}
                                                        iconSize={18}
                                                        text={displayUser.name}
                                                    />
                                                ),
                                            }}
                                            className="min-w-0 shrink-0"
                                        />
                                    </DropdownTrigger>
                                    <DropdownMenu
                                        aria-label="User Actions"
                                        variant="flat"
                                    >
                                        <DropdownItem
                                            key="profile"
                                            onPress={() => navigate("/profile")}
                                            startContent={
                                                <LuCircleUserRound size={16} />
                                            }
                                        >
                                            Profil Saya
                                        </DropdownItem>
                                        <DropdownItem
                                            key="settings"
                                            onPress={() =>
                                                navigate("/settings")
                                            }
                                            startContent={
                                                <LuSettings size={16} />
                                            }
                                        >
                                            Kelola Aplikasi
                                        </DropdownItem>
                                        <DropdownItem
                                            key="logout"
                                            color="danger"
                                            className="text-danger"
                                            startContent={
                                                <LuLogOut size={16} />
                                            }
                                            onPress={modalLogout.onOpen}
                                        >
                                            Log Out
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            ) : (
                                <Dropdown placement="bottom-end">
                                    <DropdownTrigger>
                                        <Button
                                            isIconOnly
                                            size="sm"
                                            variant="light"
                                            className="h-8 w-8 min-w-8"
                                        >
                                            <LuEllipsisVertical
                                                className="text-zinc-400"
                                                size={18}
                                            />
                                        </Button>
                                    </DropdownTrigger>
                                    <DropdownMenu
                                        aria-label="User Actions"
                                        variant="flat"
                                    >
                                        <DropdownItem
                                            key="profile"
                                            onPress={() => navigate("/profile")}
                                            startContent={
                                                <LuCircleUserRound size={16} />
                                            }
                                        >
                                            Profil Saya
                                        </DropdownItem>
                                        <DropdownItem
                                            key="settings"
                                            onPress={() =>
                                                navigate("/settings")
                                            }
                                            startContent={
                                                <LuSettings size={16} />
                                            }
                                        >
                                            Kelola Aplikasi
                                        </DropdownItem>
                                        <DropdownItem
                                            key="logout"
                                            color="danger"
                                            className="text-danger"
                                            startContent={
                                                <LuLogOut size={16} />
                                            }
                                            onPress={modalLogout.onOpen}
                                        >
                                            Log Out
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            )}
                        </div>

                        {/* Live Clock / Location Placeholder */}
                        {!isCollapsed && (
                            <div className="flex items-center justify-between text-[10px] text-zinc-500 dark:text-zinc-400">
                                <span>Jakarta, ID</span>
                                <LiveClock />
                            </div>
                        )}
                    </div>
                </div>

                {/* Logout Confirmation Modal */}
                <Modal
                    isOpen={modalLogout.isOpen}
                    onOpenChange={modalLogout.onOpenChange}
                    size="sm"
                >
                    <ModalContent>
                        {(onClose) => (
                            <>
                                <ModalHeader>
                                    <ThemedText size="md">
                                        Konfirmasi Logout
                                    </ThemedText>
                                </ModalHeader>
                                <ModalBody>
                                    <ThemedText size="sm" variant="secondary">
                                        Apakah Anda yakin ingin keluar dari
                                        aplikasi?
                                    </ThemedText>
                                </ModalBody>
                                <ModalFooter>
                                    <Button
                                        variant="light"
                                        size="sm"
                                        onPress={onClose}
                                        isDisabled={isLoggingOut}
                                    >
                                        Batal
                                    </Button>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        onPress={handleConfirmLogout}
                                        isLoading={isLoggingOut}
                                    >
                                        Keluar
                                    </Button>
                                </ModalFooter>
                            </>
                        )}
                    </ModalContent>
                </Modal>
            </aside>

            <SearchModal
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                menuItems={menuItems}
            />
        </>
    );
};

export default Sidebar;
